import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "visualize-cart";
const PENDING_CHECKOUT_KEY = "visualize-pending-checkout";

function getCartItemKey(item) {
  return `${item.productId}:${item.variantId || "base"}`;
}

function readCartItems() {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeCartItems(items) {
  if (items.length === 0) {
    window.localStorage.removeItem(STORAGE_KEY);
  } else {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }
}

export function markPendingCheckout(orderNumber) {
  window.localStorage.setItem(PENDING_CHECKOUT_KEY, JSON.stringify({ orderNumber, createdAt: Date.now() }));
}

export function clearPendingCheckout() {
  window.localStorage.removeItem(PENDING_CHECKOUT_KEY);
}

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [notice, setNotice] = useState(null);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setItems(readCartItems());
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!notice) {
      return undefined;
    }

    const timeout = window.setTimeout(() => setNotice(null), 2200);
    return () => window.clearTimeout(timeout);
  }, [notice]);

  const persist = useCallback((nextItems) => {
    setItems(nextItems);
    writeCartItems(nextItems);
  }, []);

  const addItem = useCallback((item, quantity = 1) => {
    const current = readCartItems();
    const itemKey = getCartItemKey(item);
    const existing = current.find((entry) => getCartItemKey(entry) === itemKey);
    const nextItems = existing
      ? current.map((entry) => getCartItemKey(entry) === itemKey ? { ...entry, quantity: Math.min(entry.quantity + quantity, 99) } : entry)
      : [...current, { ...item, quantity }];

    persist(nextItems);
    setNotice("Producto añadido al carrito");
  }, [persist]);

  const removeItem = useCallback((productId, variantId) => {
    persist(readCartItems().filter((item) => getCartItemKey(item) !== `${productId}:${variantId || "base"}`));
  }, [persist]);

  const updateQuantity = useCallback((productId, quantity, variantId) => {
    const nextItems = readCartItems()
      .map((item) => getCartItemKey(item) === `${productId}:${variantId || "base"}` ? { ...item, quantity } : item)
      .filter((item) => item.quantity > 0);

    persist(nextItems);
  }, [persist]);

  const clearCart = useCallback(() => {
    persist([]);
  }, [persist]);

  const itemCount = useMemo(() => items.reduce((sum, item) => sum + item.quantity, 0), [items]);
  const subtotal = useMemo(() => items.reduce((sum, item) => sum + item.quantity * item.priceCents, 0), [items]);
  const value = useMemo(() => ({ items, addItem, removeItem, updateQuantity, clearCart, itemCount, subtotal, isHydrated }), [addItem, clearCart, isHydrated, itemCount, items, removeItem, subtotal, updateQuantity]);

  return (
    <CartContext.Provider value={value}>
      {children}
      {notice ? <div style={{ position: "fixed", left: 16, right: 16, bottom: 24, zIndex: 2000, display: "flex", justifyContent: "center", pointerEvents: "none" }}><div style={{ borderRadius: 999, background: "#111", color: "white", padding: "12px 20px", border: "1px solid rgba(255, 107, 53, 0.35)", fontFamily: "Font" }}>{notice}</div></div> : null}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart debe usarse dentro de CartProvider");
  }

  return context;
}
